import { useEffect, useMemo } from 'react';
import throttle from 'lodash/throttle';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import { useHasRestoreFailed } from '../has-restore-failed/index.tsx';
import { useInProgressIssue } from '../in-progress-issue/index.tsx';

const THROTTLE_DELAY = 250;

export const useInProgressIssueSubscriptions = (): void => {
	const [
		,
		{
			onFormFieldUpdate,
			onModalOpened,
			onModalClosed,
			onModalDiscardExistingInProgressIssue,
			onFormContextUpdate,
			onTriggerIssueCreateModalStateChange,
			onStatusFieldChange,
			onClassificationLevelFieldChange,
			onResetFormFieldsForCreateAnother,
			onFormFieldConfigUpdate,
			onIssueTypeChange,
			onProjectFieldChange,
		},
	] = useInProgressIssue();
	const hasRestoreFailed = useHasRestoreFailed();

	const shouldSubscribe = !hasRestoreFailed;

	const { subscribe } = useGICLifeCycleEvents();

	const throttledOnFormFieldUpdate = useMemo(
		() => throttle(onFormFieldUpdate, THROTTLE_DELAY, { leading: true, trailing: true }),
		[onFormFieldUpdate],
	);

	useEffect(
		() =>
			shouldSubscribe
				? subscribe(GIC_LIFECYCLE_EVENTS.CREATE_FORM_FIELD_VALUE_CHANGE, throttledOnFormFieldUpdate)
				: undefined,
		[throttledOnFormFieldUpdate, subscribe, shouldSubscribe],
	);
	useEffect(
		() =>
			shouldSubscribe
				? subscribe(GIC_LIFECYCLE_EVENTS.FORM_CONTEXT_STATE_CHANGE, onFormContextUpdate)
				: undefined,
		[onFormContextUpdate, subscribe, shouldSubscribe],
	);
	useEffect(
		() =>
			shouldSubscribe ? subscribe(GIC_LIFECYCLE_EVENTS.MODAL_OPENED, onModalOpened) : undefined,
		[onModalOpened, subscribe, shouldSubscribe],
	);

	useEffect(
		() =>
			shouldSubscribe ? subscribe(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, onModalClosed) : undefined,
		[onModalClosed, shouldSubscribe, subscribe],
	);

	useEffect(
		() =>
			shouldSubscribe
				? subscribe(
						GIC_LIFECYCLE_EVENTS.RESET_FORM_FIELDS_FOR_CREATE_ANOTHER,
						onResetFormFieldsForCreateAnother,
					)
				: undefined,
		[onResetFormFieldsForCreateAnother, subscribe, shouldSubscribe],
	);

	useEffect(() => {
		shouldSubscribe
			? subscribe(
					GIC_LIFECYCLE_EVENTS.USE_TRIGGER_ISSUE_CREATE_MODAL_STATE_CHANGE,
					onTriggerIssueCreateModalStateChange,
				)
			: undefined;
	}, [onTriggerIssueCreateModalStateChange, subscribe, shouldSubscribe]);
	useEffect(
		() =>
			shouldSubscribe
				? subscribe(GIC_LIFECYCLE_EVENTS.STATUS_FIELD_STATE_CHANGE, onStatusFieldChange)
				: undefined,
		[onStatusFieldChange, subscribe, shouldSubscribe],
	);

	useEffect(
		() =>
			shouldSubscribe
				? subscribe(
						GIC_LIFECYCLE_EVENTS.CLASSIFICATION_LEVEL_FIELD_CHANGE,
						onClassificationLevelFieldChange,
					)
				: undefined,
		[onClassificationLevelFieldChange, subscribe, shouldSubscribe],
	);

	useEffect(
		() =>
			shouldSubscribe
				? subscribe(GIC_LIFECYCLE_EVENTS.CREATE_FORM_FIELD_CONFIG_UPDATE, onFormFieldConfigUpdate)
				: undefined,
		[onFormFieldConfigUpdate, subscribe, shouldSubscribe],
	);
	useEffect(
		() =>
			shouldSubscribe
				? subscribe(GIC_LIFECYCLE_EVENTS.ISSUE_TYPE_FIELD_VALUE_CHANGE, onIssueTypeChange)
				: undefined,
		[onIssueTypeChange, subscribe, shouldSubscribe],
	);
	useEffect(
		() =>
			shouldSubscribe
				? subscribe(GIC_LIFECYCLE_EVENTS.PROJECT_FIELD_VALUE_CHANGE, onProjectFieldChange)
				: undefined,
		[onProjectFieldChange, subscribe, shouldSubscribe],
	);

	useEffect(
		() =>
			shouldSubscribe
				? subscribe(
						GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_EXISTING_IN_PROGRESS_ISSUE,
						onModalDiscardExistingInProgressIssue,
					)
				: undefined,
		[onModalDiscardExistingInProgressIssue, shouldSubscribe, subscribe],
	);
};
